import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { AITransformationSchema } from "@/components/schema"
import { PageFAQ } from "@/components/PageFAQ"
import { aiTransformationFAQs } from "@/data/faqData"

const challenges = [
    {
        num: "01",
        stat: "87%",
        statLabel: "of AI pilots never reach production",
        title: "Stuck in Pilot Mode",
        description: "Many initiatives show promise in the lab but stall in production, leading to lost ROI and stalled digital transformation efforts.",
        painPoints: ["Stalled POCs", "No clear path to production", "Wasted investment"],
    },
    {
        num: "02",
        stat: "60%",
        statLabel: "of enterprise workflows remain manual",
        title: "Fragmented Processes",
        description: "Manual, disconnected workflows create organizational silos and data inconsistency that prevent AI systems from delivering unified insights.",
        painPoints: ["Data silos", "Manual handoffs", "Inconsistent outputs"],
    },
    {
        num: "03",
        stat: "3x",
        statLabel: "more costly to scale without governance",
        title: "Scaling Difficulty",
        description: "Lack of a robust AI operating model leads to governance risks, reliability issues, and difficulty maintaining trust at scale.",
        painPoints: ["Governance gaps", "Model drift", "Trust erosion"],
    },
]

const approachSteps = [
    {
        num: "01",
        title: "Readiness & Strategy",
        description: "We assess your data maturity and define a pragmatic roadmap for high-impact AI adoption.",
    },
    {
        num: "02",
        title: "Process Discovery",
        description: "We identify operations ripe for automation and design intelligent workflows to replace manual effort.",
    },
    {
        num: "03",
        title: "Governance & Integration",
        description: "We integrate models securely into your environment with strict governance and performance monitoring.",
    },
]

const automationCards = [
    {
        num: "01",
        title: "AI-Powered Workflows",
        description: "End-to-end automation of complex business processes, reducing cycle times and error rates while freeing up human talent for strategic work.",
        tags: ["Process Mining", "RPA", "Orchestration"],
    },
    {
        num: "02",
        title: "Intelligent Agents",
        description: "Deploying autonomous agents that can assist support teams, manage operations, and execute discrete tasks with high accuracy.",
        tags: ["Agentic AI", "NLP", "Task Automation"],
    },
    {
        num: "03",
        title: "Predictive Decision Systems",
        description: "Systems that analyze historical data to forecast trends, creating proactive advantages in supply chain, finance, and operations.",
        tags: ["ML Models", "Forecasting", "Analytics"],
    },
    {
        num: "04",
        title: "Human-in-the-Loop AI",
        description: "Designing interactions where AI augments human decision-making, ensuring control and accountability in critical processes.",
        tags: ["Augmented Intelligence", "Oversight", "Trust"],
    },
]

const deliverableRows = [
    [
        "AI Strategy Roadmaps",
        "Automation Blueprints",
        "Custom AI Agents",
        "LLM Fine-Tuning",
        "Predictive Models",
        "NLP Solutions",
        "Computer Vision",
        "MLOps Pipelines",
    ],
    [
        "AI Governance Frameworks",
        "Measurable ROI Analysis",
        "Change Management",
        "Data Architecture",
        "Model Monitoring",
        "Compliance Audits",
        "Responsible AI",
        "Knowledge Transfer",
    ],
    [
        "Process Mining",
        "Intelligent Workflows",
        "Agentic Orchestration",
        "Edge AI Deployment",
        "Real-Time Analytics",
        "Decision Engines",
        "API Integrations",
        "Performance Tuning",
    ],
]

const engagementSteps = [
    {
        num: "01",
        title: "Assess & Align",
    },
    {
        num: "02",
        title: "Design & Modernize",
    },
    {
        num: "03",
        title: "Operate & Optimize",
    },
]

const AIConsultingAutomation = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="AI Transformation: Strategy to Scalable Execution | RSA Tech Group"
                description="We help enterprises design, operationalize, and scale AI-driven automation with measurable, production-grade outcomes."
                image="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2670&auto=format&fit=crop"
            />
            <AITransformationSchema />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(28,43,110,0.3),transparent_60%)]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#1C2B6E]/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-4xl mx-auto">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                            AI Transformation & Automation
                        </p>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                            AI Transformation That Turns Strategy Into{" "}
                            <span className="text-[#FDA400]">Scalable Execution</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed">
                            We help enterprises design, operationalize, and scale AI-driven automation with measurable, production-grade outcomes.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                        >
                            Schedule an AI Strategy Session <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* The Challenge */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.2]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            The Problem
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            The{" "}
                            <span className="text-[#FDA400]">Challenge</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            Realizing value from AI requires moving beyond experimentation to enterprise-grade production.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {challenges.map((item) => (
                            <div
                                key={item.num}
                                className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
                            >
                                {/* Shine sweep */}
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>

                                {/* Top accent */}
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />

                                {/* Glow orb */}
                                <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/[0.10] group-hover:scale-125 transition-all duration-700" />

                                {/* Ghost number */}
                                <span className="absolute top-6 right-8 text-[120px] font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    {/* Big stat */}
                                    <div className="mb-6">
                                        <div className="text-5xl md:text-6xl font-black text-[#FDA400] leading-none mb-1">
                                            {item.stat}
                                        </div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold">
                                            {item.statLabel}
                                        </div>
                                    </div>

                                    <h3 className="text-2xl font-black text-[#1C2B6E] tracking-tight mb-3">
                                        {item.title}
                                    </h3>

                                    <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                        {item.description}
                                    </p>

                                    {/* Pain point tags */}
                                    <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-slate-100">
                                        {item.painPoints.map((point, i) => (
                                            <span
                                                key={i}
                                                className="px-3 py-1.5 bg-[#1C2B6E]/[0.04] text-[#1C2B6E]/60 text-xs font-medium rounded-full border border-[#1C2B6E]/[0.06] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                            >
                                                {point}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Strategy-Led Approach */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
                        <div>
                            <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                                Our Methodology
                            </p>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                                Strategy-Led{" "}
                                <span className="text-[#FDA400]">AI Approach</span>
                            </h2>
                            <p className="text-slate-400 text-lg font-light leading-relaxed mb-10">
                                We don't just deploy technology. We align AI strategy with your business goals, ensuring every automation initiative drives tangible value.
                            </p>

                            <div className="space-y-8">
                                {approachSteps.map((step) => (
                                    <div key={step.num} className="flex gap-5 group">
                                        <div className="w-12 h-12 bg-[#FDA400]/10 border border-[#FDA400]/20 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-black text-[#FDA400]">{step.num}</span>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-white tracking-tight mb-2">{step.title}</h3>
                                            <p className="text-slate-400 text-sm font-light leading-relaxed">{step.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Pipeline visualization */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-[#FDA400]/[0.03] rounded-3xl blur-[60px]" />
                            <div className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10">
                                <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-4">
                                    <div className="text-xs text-slate-400 uppercase tracking-[0.2em] font-bold">Automation Pipeline</div>
                                    <div className="flex items-center gap-2">
                                        <span className="relative flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-40" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#FDA400] text-xs font-bold">Active</span>
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    {[
                                        { step: "01", label: "Opportunity Analysis", status: "Complete" },
                                        { step: "02", label: "Workflow Design", status: "Complete" },
                                        { step: "03", label: "Model Deployment", status: "In Progress" },
                                    ].map((item, i) => (
                                        <div key={i}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black ${
                                                        item.status === "In Progress"
                                                            ? "bg-[#FDA400] text-[#1C2B6E]"
                                                            : "bg-white/[0.06] text-slate-300 border border-white/10"
                                                    }`}>
                                                        {item.step}
                                                    </div>
                                                    <span className={`text-sm ${item.status === "In Progress" ? "text-white font-bold" : "text-slate-300 font-medium"}`}>
                                                        {item.label}
                                                    </span>
                                                </div>
                                                <span className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
                                                    item.status === "In Progress"
                                                        ? "bg-[#FDA400]/10 text-[#FDA400] border border-[#FDA400]/20"
                                                        : "bg-white/[0.04] text-slate-400 border border-white/10"
                                                }`}>
                                                    {item.status}
                                                </span>
                                            </div>
                                            {i < 2 && <div className="w-0.5 h-4 bg-white/10 ml-4 mt-2" />}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Intelligent Automation in Practice */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.2]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Capabilities
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            Intelligent Automation{" "}
                            <span className="text-[#FDA400]">in Practice</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            We apply AI where it creates operational leverage, not experimentation overhead.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                        {automationCards.map((item) => (
                            <div
                                key={item.num}
                                className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
                            >
                                {/* Shine sweep */}
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>

                                {/* Top accent */}
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />

                                {/* Glow */}
                                <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

                                {/* Ghost number */}
                                <span className="absolute top-4 right-6 text-8xl font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.07] transition-colors duration-500">
                                    {item.num}
                                </span>

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-5">
                                        {item.num}
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-black text-[#1C2B6E] tracking-tight mb-3">
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                        {item.description}
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-slate-100">
                                        {item.tags.map((tag, i) => (
                                            <span
                                                key={i}
                                                className="px-3 py-1.5 bg-[#1C2B6E]/[0.04] text-[#1C2B6E]/70 text-xs font-medium rounded-full border border-[#1C2B6E]/[0.06] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* What We Deliver */}
            <section className="py-24 bg-gradient-to-b from-white via-slate-50 to-white relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Deliverables
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            What We{" "}
                            <span className="text-[#FDA400]">Deliver</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            Concrete deliverables aligned to business objectives, not abstract capabilities.
                        </p>
                    </div>
                </div>

                {/* Scrolling marquee rows */}
                <div className="relative z-10 space-y-4">
                    {/* Fade edges */}
                    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-50 via-slate-50/80 to-transparent z-20 pointer-events-none" />
                    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-50 via-slate-50/80 to-transparent z-20 pointer-events-none" />

                    {deliverableRows.map((row, rowIndex) => (
                        <div key={rowIndex} className="overflow-hidden">
                            <div
                                className="flex gap-4 w-max"
                                style={{
                                    animation: `${rowIndex % 2 === 0 ? "scrollLeft" : "scrollRight"} ${30 + rowIndex * 5}s linear infinite`,
                                }}
                            >
                                {/* Duplicate for seamless loop */}
                                {[...row, ...row].map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-3 px-6 py-4 bg-white rounded-2xl border border-[#1C2B6E]/[0.06] shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:border-[#FDA400]/30 hover:shadow-[0_12px_40px_rgba(28,43,110,0.1)] transition-all duration-300 group cursor-default flex-shrink-0"
                                    >
                                        <span className="relative flex h-2 w-2 flex-shrink-0">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-30 group-hover:animate-ping" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#1C2B6E] font-bold text-sm whitespace-nowrap group-hover:text-[#FDA400] transition-colors duration-300">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Enterprise Ready + Engagement Model */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    {/* Enterprise Ready */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Built for Scale
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                            Enterprise Ready{" "}
                            <span className="text-[#FDA400]">by Design</span>
                        </h2>
                        <p className="text-slate-400 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            We ensure your AI adoption is secure, compliant, and compatible with your existing infrastructure.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-24">
                        {[
                            { num: "01", title: "Legacy & Cloud Compatible", desc: "We bridge the gap between modern AI models and established enterprise systems." },
                            { num: "02", title: "Secure & Private", desc: "Deploying private models that protect your IP and adhere to regulatory standards." },
                        ].map((item) => (
                            <div
                                key={item.num}
                                className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10 hover:bg-white/[0.07] hover:border-[#FDA400]/20 transition-all duration-500 group"
                            >
                                <span className="absolute top-4 right-6 text-7xl font-black text-white/[0.03] leading-none select-none group-hover:text-white/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>
                                <div className="relative z-10">
                                    <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-4">
                                        {item.num}
                                    </div>
                                    <h3 className="text-2xl font-black text-white tracking-tight mb-3">{item.title}</h3>
                                    <p className="text-slate-400 text-sm font-light leading-relaxed">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Engagement Model */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Our Process
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight leading-[1.1]">
                            Strategy-Led{" "}
                            <span className="text-[#FDA400]">Engagement</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 relative max-w-5xl mx-auto">
                        <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-[#FDA400]/40 via-[#1C2B6E]/30 to-[#FDA400]/40 z-0" />

                        {engagementSteps.map((step) => (
                            <div key={step.num} className="relative z-10 text-center">
                                <div className="w-28 h-28 mx-auto bg-[#0f1629] border-2 border-[#FDA400]/30 rounded-full flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(253,164,0,0.1)]">
                                    <span className="text-3xl font-black text-[#FDA400]">{step.num}</span>
                                </div>
                                <h3 className="text-xl font-black text-white tracking-tight">
                                    {step.title}
                                </h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />

                <div className="section-container relative z-10 text-center">
                    <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                        Get Started
                    </p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1] mb-6">
                        Start Your AI{" "}
                        <span className="text-[#FDA400]">Transformation</span>
                    </h2>
                    <p className="text-slate-500 text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                        Move from strategy to scalable execution with a trusted partner.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                    >
                        Schedule an AI Strategy Session <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>
            </section>

            {/* FAQ Section */}
            <PageFAQ
                faqs={aiTransformationFAQs}
                title="AI Transformation FAQs"
                subtitle="Common questions about enterprise AI transformation and automation"
                variant="muted"
            />

            <Footer />
        </div>
    )
}

export default AIConsultingAutomation
